import React, { ReactElement, useEffect, useState } from 'react';
import FormTreeSelector from 'adapter/in/common/components/TreeSelector';
import { getErrorMessage, onChangeSelect, SelectorContainerProps } from 'adapter/in/common/form/Selector';
import { RulesRHF, SelectorFieldDataImpl, SelectorType, TreeSelectorOption } from 'adapter/in/common/form/types';
import { isSelectorFieldDataValid, useBaseSelectorContainer } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import i18n from 'asset/translation';
import { useBaseDashboardsApiSelector } from 'adapter/in/common/hooks/form/useBaseDashboardsApiSelector';
import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';

export const ApiTreeSelectorContainer = ({
    name,
    label,
    configType,
    dataDependsOn,
    affectsTo,
    disabled,
    pageSize,
    footer,
}: SelectorContainerProps): ReactElement => {
    const [rules, setRules] = useState<RulesRHF>({
        required: i18n.t('widgets.errorNotEmpty').toString(),
        validate: (v: SelectorFieldDataImpl) => isSelectorFieldDataValid(1, 100)(v),
    });
    const { value, onChange, invalid, error, shouldLoad, dependantValues, register, trigger } = useBaseSelectorContainer(
        name,
        rules,
        undefined,
        dataDependsOn,
        affectsTo
    );
    const { setHasChange: setWidgetMenuHasChange } = dispatchFormChangesView();
    const { isLoading, maxSelectionLength, minSelectionLength, options } = useBaseDashboardsApiSelector<TreeSelectorOption>(
        name,
        SelectorType.TREE,
        configType,
        pageSize ?? 0,
        value,
        shouldLoad,
        dependantValues,
        (updatedValue) => {
            onChange(updatedValue);
        }
    );

    useEffect(() => {
        const rulesToSet = {
            required: minSelectionLength > 0 ? i18n.t('widgets.errorNotEmpty').toString() : false,
            validate: (v: SelectorFieldDataImpl) => isSelectorFieldDataValid(minSelectionLength, maxSelectionLength)(v),
        };
        setRules(rulesToSet);
        register(name, rulesToSet);
        trigger(name);
    }, [minSelectionLength, maxSelectionLength]);

    const hasError =
        (invalid && !!error && error.type !== 'loading' && options && options.length > 0) ||
        !isSelectorFieldDataValid(minSelectionLength, maxSelectionLength)(value);

    return (
        <FormTreeSelector
            isLoading={isLoading}
            name={name}
            label={label}
            placeholder={
                minSelectionLength === 0 ? i18n.t('common.selector.placeholder.optional') : i18n.t('common.selector.placeholder.required')
            }
            disabled={disabled}
            maxSelectionLength={maxSelectionLength}
            minSelectionLength={minSelectionLength}
            value={value?.value}
            error={hasError}
            tree={options}
            onChange={(selectedValues: string[]) => {
                setWidgetMenuHasChange(true);
                onChangeSelect(minSelectionLength, maxSelectionLength, configType, value)(selectedValues, onChange);
            }}
            errorMessage={hasError ? getErrorMessage(value.value, minSelectionLength, maxSelectionLength) : ''}
            footer={footer}
        />
    );
};
